/**
 * Animal Health Certificate (AHC) questionnaire — interactive form for
 * populating fields inline before generating the PDF.
 */
import { DatePicker } from '@/frontend/components/date-picker'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import { Input } from '@/frontend/components/ui/input'
import { Label } from '@/frontend/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/frontend/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'
import {
  AHC_VALIDITY_TEXTS,
  type AhcQuestionnaireFields,
  AhcQuestionnairePdf,
  AUTHORISED_PERSON_NOTE,
  DECLARATION_TEXT,
  defaultAhcQuestionnaireFields,
  EU_COUNTRIES,
  FIRST_EU_COUNTRY_INFO,
  FIRST_EU_COUNTRY_INFO_ITALIC,
  formatCountryLabel,
  INTRO_TEXT,
  OWNER_NOT_JOINING_NOTE,
  PET_COUNT_OPTIONS,
  PET_SEX_OPTIONS,
  PETS_NOTE,
  RABIES_NOT_VACCINATED_NOTE,
  RABIES_OPTIONS,
  RABIES_PROOF_NOTE,
  TAPEWORM_COUNTRIES_NOTE,
  TRAVEL_METHODS,
} from '@/frontend/features/printing/documents/ahc-questionnaire.print'
import { ClinicHeaderPreview } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { DocumentPreview } from '@/frontend/features/printing/documents/blocks/document-preview'
import {
  formatCalendarDate,
  resolveDateFormat,
} from '@/frontend/features/printing/documents/blocks/format-utils'
import { OwnerSignaturePreview } from '@/frontend/features/printing/documents/blocks/owner-signature'
import type { DocumentFormProps } from '@/frontend/features/printing/documents/types'
import { withDocumentEditor } from '@/frontend/features/printing/documents/with-document-editor'

/** Labelled text input with an inline layout matching the printed form. */
function InlineInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-semibold">{label}</span>
      <Input
        className="w-64"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  )
}

function AhcQuestionnaireForm({
  context,
  value,
  onChange,
}: DocumentFormProps<AhcQuestionnaireFields>) {
  const { patient, owner, clinic } = context
  // The preview is WYSIWYG with the PDF, so it follows the *document* rather than the
  // viewer: same resolution as the PDF half, off the owning member's region. Otherwise a
  // UK vet previewing a US member's certificate sees a different date order on screen
  // than in the file they download. DOB is a calendar day, so only the locale applies.
  const fmt = resolveDateFormat(null, owner.region)

  const countrySelectItems = EU_COUNTRIES.map((c) => ({
    value: c.code,
    label: formatCountryLabel(c),
  }))

  return (
    <DocumentPreview>
      <ClinicHeaderPreview clinic={clinic} />

      {/* Intro */}
      {INTRO_TEXT.map((line) => (
        <p key={line}>{line}</p>
      ))}

      {/* Q1–3 */}
      <div className="mt-6 space-y-3">
        <InlineInput
          label="1. What is your full name?:"
          value={value.fullName}
          onChange={(v) => onChange({ ...value, fullName: v })}
          placeholder={`${owner.firstName} ${owner.lastName}`}
        />
        <InlineInput
          label="2. Please provide the name of the pet(s) you are travelling with:"
          value={value.petNames}
          onChange={(v) => onChange({ ...value, petNames: v })}
          placeholder={patient.name}
        />
        <InlineInput
          label="3. Email address:"
          value={value.emailAddress}
          onChange={(v) => onChange({ ...value, emailAddress: v })}
          placeholder={owner.email}
        />
      </div>

      {/* Q4: Travelling person */}
      <div className="mt-6">
        <p>
          4. Please describe <strong>who</strong> will be accompanying the pet(s) travelling.{' '}
          <em>
            Please note that the owner or authorised person (who holds a written authorisation from
            the owner) must travel within <strong>five</strong> days of the animal's movement.
          </em>
        </p>
        <RadioGroup
          className="mt-2"
          value={value.travellingPerson}
          onValueChange={(v) =>
            onChange({ ...value, travellingPerson: v as 'owner' | 'authorised' })
          }
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="owner" id="tp-owner" />
            <Label htmlFor="tp-owner" className="font-normal">
              Owner
            </Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="authorised" id="tp-authorised" />
            <Label htmlFor="tp-authorised" className="font-normal">
              Authorised person
            </Label>
          </div>
        </RadioGroup>

        {value.travellingPerson === 'authorised' && (
          <div className="mt-3 space-y-3 pl-6">
            <p className="text-sm italic text-muted-foreground">{AUTHORISED_PERSON_NOTE}</p>
            <InlineInput
              label="- Please describe the authorised person's relationship with the owner:"
              value={value.authorisedRelationship}
              onChange={(v) => onChange({ ...value, authorisedRelationship: v })}
            />
            <div>
              <p>
                - Will the owner also be joining this trip/ arriving separately within 5 days of
                departure?
              </p>
              <RadioGroup
                className="mt-1"
                value={value.ownerJoiningWithin5Days}
                onValueChange={(v) =>
                  onChange({ ...value, ownerJoiningWithin5Days: v as 'yes' | 'no' })
                }
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="yes" id="oj-yes" />
                  <Label htmlFor="oj-yes" className="font-normal">
                    Yes
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="no" id="oj-no" />
                  <Label htmlFor="oj-no" className="font-normal">
                    No*
                  </Label>
                </div>
              </RadioGroup>
              <p className="mt-1 text-sm italic text-muted-foreground">{OWNER_NOT_JOINING_NOTE}</p>
            </div>
          </div>
        )}
      </div>

      <hr className="my-6" />

      {/* TRIP DETAILS */}
      <h2 className="text-headline-md">TRIP DETAILS:</h2>

      <InlineInput
        label="1. Destination of trip:"
        value={value.destination}
        onChange={(v) => onChange({ ...value, destination: v })}
        placeholder="Please enter the EU country you are travelling to"
      />

      <div className="mt-4">
        <p>
          1. <strong>First EU country</strong> you will enter on your trip? {FIRST_EU_COUNTRY_INFO}
        </p>
        <p className="mt-1 text-sm italic text-muted-foreground">{FIRST_EU_COUNTRY_INFO_ITALIC}</p>
        <div className="mt-2">
          <Select
            value={value.firstEuCountry || null}
            onValueChange={(v) => onChange({ ...value, firstEuCountry: v ?? '' })}
            items={countrySelectItems}
          >
            <SelectTrigger className="w-80">
              <SelectValue placeholder="Select country…" />
            </SelectTrigger>
            <SelectContent>
              {EU_COUNTRIES.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {formatCountryLabel(c)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4">
        <p className="font-semibold">How will your pet(s) be getting there?</p>
        <RadioGroup
          className="mt-1"
          value={value.travelMethod}
          onValueChange={(v) =>
            onChange({ ...value, travelMethod: v as AhcQuestionnaireFields['travelMethod'] })
          }
        >
          {TRAVEL_METHODS.map((m) => (
            <div key={m.value} className="flex items-center gap-2">
              <RadioGroupItem value={m.value} id={`tm-${m.value}`} />
              <Label htmlFor={`tm-${m.value}`} className="font-normal">
                {m.label}
              </Label>
              {m.value === 'other' && value.travelMethod === 'other' && (
                <Input
                  className="ml-2 w-48"
                  value={value.travelMethodOther}
                  onChange={(e) => onChange({ ...value, travelMethodOther: e.target.value })}
                  placeholder="Please specify"
                />
              )}
            </div>
          ))}
        </RadioGroup>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="font-semibold">Date you are departing (dd/mm/yyyy):</span>
        <DatePicker
          value={value.departureDate ?? undefined}
          onChange={(d) => onChange({ ...value, departureDate: d ?? null })}
          placeholder="Select date"
          className="w-48"
        />
      </div>

      <div className="mt-4">
        <p className="font-semibold">Date you plan to return:</p>
        <RadioGroup
          className="mt-1"
          value={value.returnType}
          onValueChange={(v) => onChange({ ...value, returnType: v as 'date' | 'indefinite' })}
        >
          <div className="flex items-center gap-2">
            <RadioGroupItem value="date" id="rt-date" />
            <Label htmlFor="rt-date" className="font-normal">
              (dd/mm/yyyy):
            </Label>
            {value.returnType === 'date' && (
              <DatePicker
                value={value.returnDate ?? undefined}
                onChange={(d) => onChange({ ...value, returnDate: d ?? null })}
                placeholder="Select date"
                className="w-48"
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem value="indefinite" id="rt-indef" />
            <Label htmlFor="rt-indef" className="font-normal">
              I plan to move away indefinitely
            </Label>
          </div>
        </RadioGroup>
      </div>

      <div className="mt-4 space-y-1 text-sm italic text-muted-foreground">
        {AHC_VALIDITY_TEXTS.map((text) => (
          <p key={text.slice(0, 30)}>{text}</p>
        ))}
        <p className="font-semibold">{TAPEWORM_COUNTRIES_NOTE}</p>
      </div>

      <hr className="my-6" />

      {/* OWNER DETAILS */}
      <h2 className="text-headline-md">OWNER DETAILS:</h2>

      <div className="space-y-3">
        <InlineInput
          label="Owner's Full Name:"
          value={value.ownerFullName}
          onChange={(v) => onChange({ ...value, ownerFullName: v })}
          placeholder={`${owner.firstName} ${owner.lastName}`}
        />
        <InlineInput
          label="Owner's full UK address:"
          value={value.ownerAddress}
          onChange={(v) => onChange({ ...value, ownerAddress: v })}
          placeholder={[owner.address1, owner.address2, owner.city].filter(Boolean).join(', ')}
        />
        <InlineInput
          label="Owner's UK postcode:"
          value={value.ownerPostcode}
          onChange={(v) => onChange({ ...value, ownerPostcode: v })}
          placeholder={owner.postalCode ?? undefined}
        />
        <InlineInput
          label="Owner's UK mobile phone number:"
          value={value.ownerPhone}
          onChange={(v) => onChange({ ...value, ownerPhone: v })}
          placeholder={owner.phone}
        />
      </div>

      <hr className="my-6" />

      {/* PET DETAILS */}
      <h2 className="text-headline-md">PET DETAILS:</h2>

      <div>
        <p className="font-semibold">How many pets are you travelling with?</p>
        <RadioGroup
          className="mt-1"
          value={value.petCount}
          onValueChange={(v) => onChange({ ...value, petCount: v })}
        >
          {PET_COUNT_OPTIONS.map((n) => (
            <div key={n} className="flex items-center gap-2">
              <RadioGroupItem value={n} id={`pc-${n}`} />
              <Label htmlFor={`pc-${n}`} className="font-normal">
                {n}
              </Label>
            </div>
          ))}
        </RadioGroup>
        <p className="mt-1 text-sm italic text-muted-foreground">{PETS_NOTE}</p>
      </div>

      <div className="mt-4 space-y-3">
        <InlineInput
          label="Please confirm your pet's name:"
          value={value.petName}
          onChange={(v) => onChange({ ...value, petName: v })}
          placeholder={patient.name}
        />

        <div>
          <p className="font-semibold">Please confirm your pet's sex:</p>
          <RadioGroup
            className="mt-1"
            value={value.petSex}
            onValueChange={(v) =>
              onChange({ ...value, petSex: v as AhcQuestionnaireFields['petSex'] })
            }
          >
            {PET_SEX_OPTIONS.map((o) => (
              <div key={o.value} className="flex items-center gap-2">
                <RadioGroupItem value={o.value} id={`ps-${o.value}`} />
                <Label htmlFor={`ps-${o.value}`} className="font-normal">
                  {o.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <InlineInput
          label="Please confirm what colour your pet is:"
          value={value.petColour}
          onChange={(v) => onChange({ ...value, petColour: v })}
        />

        <InlineInput
          label="Please confirm the breed of your pet:"
          value={value.petBreed}
          onChange={(v) => onChange({ ...value, petBreed: v })}
          placeholder={patient.breed ?? undefined}
        />

        <div>
          <InlineInput
            label="Microchip number:"
            value={value.microchipNumber}
            onChange={(v) => onChange({ ...value, microchipNumber: v })}
            placeholder={patient.microchipNumber ?? undefined}
          />
          {patient.microchipNumber && (
            <p className="mt-1 text-sm text-muted-foreground">
              If we have this number on file, it has been saved as: {patient.microchipNumber}.
            </p>
          )}
        </div>

        <div>
          <InlineInput
            label="Please confirm your pet's date of birth (dd/mm/yyyy):"
            value={value.dateOfBirth}
            onChange={(v) => onChange({ ...value, dateOfBirth: v })}
            placeholder={
              patient.dateOfBirth ? formatCalendarDate(patient.dateOfBirth, fmt) : undefined
            }
          />
          {patient.dateOfBirth && (
            <p className="mt-1 text-sm text-muted-foreground">
              The DOB we have on file is: {formatCalendarDate(patient.dateOfBirth, fmt)}.
            </p>
          )}
        </div>
      </div>

      {/* Rabies */}
      <div className="mt-4">
        <p className="font-semibold">Is your pet up to date with their rabies vaccinations?</p>
        <RadioGroup
          className="mt-1"
          value={value.rabiesVaccination}
          onValueChange={(v) =>
            onChange({
              ...value,
              rabiesVaccination: v as AhcQuestionnaireFields['rabiesVaccination'],
            })
          }
        >
          {RABIES_OPTIONS.map((o) => (
            <div key={o.value} className="flex items-center gap-2">
              <RadioGroupItem value={o.value} id={`rv-${o.value}`} />
              <Label htmlFor={`rv-${o.value}`} className="font-normal">
                {o.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
        <div className="mt-2 space-y-1 text-sm italic text-muted-foreground">
          <p>{RABIES_PROOF_NOTE}</p>
          <p>{RABIES_NOT_VACCINATED_NOTE}</p>
        </div>
      </div>

      <hr className="my-6" />

      {/* DECLARATION */}
      <h2 className="text-headline-md">DECLARATION:</h2>

      <div className="flex items-start gap-2">
        <Checkbox
          checked={value.declarationAccepted}
          onCheckedChange={(checked) =>
            onChange({ ...value, declarationAccepted: checked === true })
          }
          id="declaration"
        />
        <Label htmlFor="declaration" className="font-normal leading-snug">
          {DECLARATION_TEXT}
        </Label>
      </div>

      <OwnerSignaturePreview owner={context.owner} label="Name" prefillName={false} />

      <hr className="my-6" />

      <p className="italic text-muted-foreground">
        Thank you for completing this form. Please reach out with any questions.
      </p>
    </DocumentPreview>
  )
}

export const ahcQuestionnaire = withDocumentEditor({
  id: 'ahc-questionnaire',
  label: 'Animal Health Certificate Questionnaire',
  defaultFields: defaultAhcQuestionnaireFields,
  Preview: AhcQuestionnaireForm,
  Pdf: AhcQuestionnairePdf,
})
